import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClick", "isActive", "variant", "disabled", "fullBorder", "label", "index", "refCallback"],
    _excluded2 = ["fullBorder", "children", "toggleTabCallback", "data-test", "activeTabFromProp"];

/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './Tabs.styles';
import { getNextAvailableTabLeftDirection, getNextAvailableTabRightDirection } from './helper';
var keyCodes = {
  LEFT: 37,
  RIGHT: 39
};

var Tab = function Tab(_ref) {
  var _onClick = _ref.onClick,
      isActive = _ref.isActive,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'tab' : _ref$variant,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      fullBorder = _ref.fullBorder,
      label = _ref.label,
      index = _ref.index,
      refCallback = _ref.refCallback,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(Styled.Tab, _extends({
    variant: variant,
    isActive: isActive,
    disabled: disabled,
    fullBorder: fullBorder,
    onClick: function onClick(event) {
      event.preventDefault();

      _onClick(index);
    },
    id: "tab-".concat(index),
    key: "tab-".concat(index),
    role: "tab",
    tabIndex: 0,
    href: "#",
    "aria-selected": isActive,
    "aria-disabled": disabled,
    "aria-controls": "tabpanel-".concat(index),
    ref: refCallback
  }, props), label);
};

var Tabs = function Tabs(_ref2) {
  var fullBorder = _ref2.fullBorder,
      children = _ref2.children,
      toggleTabCallback = _ref2.toggleTabCallback,
      dataTest = _ref2['data-test'],
      _ref2$activeTabFromPr = _ref2.activeTabFromProp,
      activeTabFromProp = _ref2$activeTabFromPr === void 0 ? 0 : _ref2$activeTabFromPr,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var _useState = useState(activeTabFromProp),
      _useState2 = _slicedToArray(_useState, 2),
      activeTab = _useState2[0],
      toggleActiveTab = _useState2[1];

  var tabListLength = children.length - 1;
  useEffect(function () {
    return toggleActiveTab(activeTabFromProp);
  }, [activeTabFromProp]);

  var renderChildren = function renderChildren() {
    return React.Children.map(children, function (child, index) {
      return /*#__PURE__*/React.cloneElement(child, {
        onClick: function onClick() {
          !child.props.disabled && toggleActiveTab(index);
          index !== activeTab && toggleTabCallback(index);
        },
        onKeyDown: function onKeyDown(evt) {
          var nextIndex;

          if (evt.keyCode === keyCodes.LEFT) {
            nextIndex = getNextAvailableTabLeftDirection(activeTab, tabListLength, children);
            toggleActiveTab(nextIndex);
            toggleTabCallback(nextIndex);
          }

          if (evt.keyCode === keyCodes.RIGHT) {
            nextIndex = getNextAvailableTabRightDirection(activeTab, tabListLength, children);
            toggleActiveTab(nextIndex);
            toggleTabCallback(nextIndex);
          }
        },
        isActive: index === activeTab,
        fullBorder: fullBorder,
        index: index,
        tabIndex: index === activeTab ? 0 : -1,
        'data-test': "".concat(dataTest, "-tab-").concat(index)
      });
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled.Tabs, _extends({
    fullBorder: fullBorder,
    role: "tablist",
    "data-test": dataTest
  }, props), renderChildren()), /*#__PURE__*/React.createElement(Styled.Content, {
    id: "tabpanel-".concat(activeTab),
    role: "tabpanel",
    tabIndex: "0",
    "aria-labelledby": "tab-".concat(activeTab)
  }, children[activeTab].props.children));
};

Tabs.displayName = 'Tabs';
Tab.displayName = 'Tab';
Tabs.propTypes = {
  /**
   * Optional prop to specify if the tabs should have the full border style
   */
  fullBorder: PropTypes.bool,

  /**
   * Optional prop to specify any callbacks you want when a Tab is clicked. Passes back the Tab index
   */
  toggleTabCallback: PropTypes.func,

  /**
   * Specify an optional data-test attribute to be added to your Alert for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional prop to be able to change the active tab from outside the component
   */
  activeTabFromProp: PropTypes.number
};
Tabs.defaultProps = {
  fullBorder: false,
  toggleTabCallback:
  /* istanbul ignore next */
  function toggleTabCallback() {},
  'data-test': 'component-tabs'
};
export { Tabs, Tab };